import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Card, CardContent, Typography, RadioGroup, FormControlLabel, Radio, Button, Alert, LinearProgress } from "@mui/material";
import AdminLayout from "../AdminLayout";
import { getEvent, saveQuizAttempt, registerForEvent } from "../../services/eventV2Service";
import { useAuth } from "../../hooks/useAuth";
import creditService from "../../services/creditService";
import { validateQuizJSON, computeScore } from "../../utils/quizSchema";

export default function EventQuizPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [scorePct, setScorePct] = useState(null);

  useEffect(() => {
    (async () => {
      const ev = await getEvent(eventId);
      setEvent(ev);
    })();
  }, [eventId]);

  const score = useMemo(() => {
    if (!event?.quiz?.questions) return 0;
    const res = validateQuizJSON(event.quiz);
    if (!res.valid) return 0;
    const totalCredit = computeScore(answers, event.quiz.questions);
    // Convert to percentage based on equal weighting if credits omitted: computeScore already handles default 100/len
    return Math.min(100, Math.max(0, totalCredit));
  }, [answers, event]);

  const handleSubmit = async () => {
    setError("");
    if (!event?.quiz?.questions) {
      setError("Quiz not configured");
      return;
    }
    if (!user?.uid) {
      setError("Not authenticated");
      return;
    }
    const qCount = event.quiz.questions.length;
    if (Object.keys(answers).length !== qCount) {
      setError("Answer all questions");
      return;
    }
    setSubmitting(true);
    try {
      const passed = score >= (event.cutoff || 0);
      await saveQuizAttempt(user.uid, eventId, score, passed);
      setScorePct(score);
      if (passed) {
        try {
          // Award credits based on per-question credit sum; prevent duplicates via metadata.activityId
          const totalAwardCredit = computeScore(answers, event.quiz.questions);
          await creditService.awardAutoCredits(user.uid, "quiz_completed", eventId, Math.round(totalAwardCredit));
        } catch (_) { }
        navigate(`/student/events-v2/${eventId}/register`);
      }
    } catch (e) {
      setError(e.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminLayout title="Event Quiz">
      <Box sx={{ p: 3 }}>
        <Card sx={{ borderTop: "6px solid #003366" }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#003366", mb: 1 }}>
              Qualification Quiz
            </Typography>
            {event && (
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                You must score at least {event.cutoff || 0}% to unlock registration. Questions: {event.quiz?.questions?.length || 0}
              </Typography>
            )}
            {!event?.quiz?.questions ? (
              <Typography color="text.secondary">Quiz not available</Typography>
            ) : (
              <>
                {scorePct !== null && (
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress variant="determinate" value={scorePct} sx={{ height: 10, borderRadius: 2 }} />
                    <Typography color="text.secondary" sx={{ mt: 1 }}>Score: {scorePct}%</Typography>
                  </Box>
                )}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {event.quiz.questions.map((q, idx) => (
                  <Box key={q.id || idx} sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 2, mb: 1 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>{idx + 1}. {(q.text ?? q.question)}</Typography>
                    {(!q.id) && (
                      <Typography variant="caption" color="text.secondary">Note: question id autogenerated</Typography>
                    )}
                    <RadioGroup
                      value={answers[q.id ?? `q${idx}`] || ""}
                      onChange={(e) => setAnswers(prev => ({ ...prev, [q.id ?? `q${idx}`]: e.target.value }))}
                    >
                      {q.options.map((opt, i) => (
                        <FormControlLabel key={i} value={opt} control={<Radio />} label={opt} />
                      ))}
                    </RadioGroup>
                  </Box>
                ))}
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Button variant="contained" disabled={submitting} onClick={handleSubmit}>
                    {submitting ? "Submitting..." : "Submit"}
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
}
